import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';
import { clearPatientTabsStorage } from '../components/PatientTabs/PatientTabsContext';

// Environment Variables Debug Logging
const authDomain = process.env.REACT_APP_AUTH0_DOMAIN;
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
const redirectUri = process.env.REACT_APP_AUTH0_REDIRECT_URI;
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

console.log('🔧 AuthContext Environment Variables:');
console.log('Auth0 Domain:', authDomain);
console.log('Client ID:', clientId);
console.log('Redirect URI:', redirectUri);
console.log('API Base URL:', apiBaseUrl);

// Inactivity timeout: 10 minutes in milliseconds (HIPAA screen-lock requirement)
const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  roles?: string[];
  is_internal?: boolean;
  is_clinic_admin?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  checkAuthStatus: () => Promise<void>;
  isSessionWarning: boolean;
}

export const AuthContext = createContext<AuthContextType | null>(null);

const extractRoles = (userData: any): string[] => {
  const rolesClaim = userData?.roles ?? userData?.['https://phanova.com/roles'];
  const roles = Array.isArray(rolesClaim)
    ? rolesClaim
    : rolesClaim
      ? [rolesClaim]
      : [];
  const singleRole = userData?.['https://phanova.com/role'] ?? userData?.role;
  if (singleRole && !roles.includes(singleRole)) {
    roles.push(singleRole);
  }
  return roles.filter(Boolean);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const saved = localStorage.getItem('phanova_user');
      if (saved && saved !== 'null' && saved !== 'undefined') {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.id) return parsed;
      }
    } catch {}
    return null;
  });
  const [loading, setLoading] = useState(true);
  const isInitializedRef = useRef(false);
  // Guard against the four concurrent logout paths (inactivity, countdown→0, 10s auto-lock,
  // extend-failure) all racing to call performLogout simultaneously.
  const isLoggingOutRef = useRef(false);

  // Logout function that will be used by both manual logout and inactivity timeout
  // Memoized to prevent unnecessary re-renders in dependent hooks
  const performLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    console.log('🚪 Starting logout...');
    console.log('🔧 Using API Base URL:', apiBaseUrl);

    // Clear local state first
    localStorage.removeItem('phanova_user');
    localStorage.removeItem('phanova_auth_attempt');
    clearPatientTabsStorage();
    setUser(null);

    // Full logout: navigate the top-level browser to the BFF /logout endpoint.
    // The BFF destroys the Express session and then 302-redirects to Auth0's
    // /v2/logout, which clears Auth0's own SSO cookie. This MUST be a real
    // navigation (not fetch) — a cross-origin XHR follows the redirect inside
    // the request and never clears the Auth0 SSO cookie, leaving the user able
    // to silently re-authenticate into the same account. Auth0 redirects back
    // to FRONTEND_URL (returnTo) which lands on the login screen.
    // This path is shared by manual logout, the 5-min inactivity lock, and the
    // session timeout, so all of them now perform a full Auth0 logout.
    const baseUrl = apiBaseUrl || 'http://localhost:5802';
    const logoutUrl = `${baseUrl}/logout`;
    console.log('🔗 Redirecting to full logout:', logoutUrl);
    window.location.href = logoutUrl;
  }, []);

  // Memoized callbacks for timeout hooks to prevent recreation on every render
  const handleInactivityTimeout = useCallback(() => {
    console.log('⏰ User inactive for 10 minutes - logging out automatically');
    performLogout();
  }, [performLogout]);

  const handleSessionTimeout = useCallback(() => {
    console.log('⏰ Session timeout reached - logging out');
    performLogout();
  }, [performLogout]);

  // Setup inactivity timeout - only active when user is logged in
  useInactivityTimeout({
    onTimeout: handleInactivityTimeout,
    timeoutMs: INACTIVITY_TIMEOUT_MS,
    enabled: !!user && !loading, // Only enable when user is authenticated and not loading
  });

  // Setup session timeout management with automatic extension
  const {
    remainingSeconds,
    isWarningShown,
    extendSession,
  } = useSessionTimeout({
    enabled: !!user && !loading, // Only enable when user is authenticated and not loading
    onTimeout: handleSessionTimeout,
    warningThresholdSeconds: 60, // Show warning at 1 minute remaining
    extendCooldownMs: 5000, // 5 seconds between extends
    checkIntervalSeconds: 15, // Check timeout info every 15 seconds
  });

  // Memoize checkAuthStatus to prevent unnecessary re-renders
  const checkAuthStatus = useCallback(async () => {
    setLoading(true);
    try {
      console.log('=== Checking Auth Status ===');
      console.log('Current URL:', window.location.href);
      
      // First check localStorage for existing session
      const savedUser = localStorage.getItem('phanova_user');
      let cachedUser = null;
      if (savedUser && savedUser !== 'null' && savedUser !== 'undefined') {
        try {
          const parsedUser = JSON.parse(savedUser);
          if (parsedUser && parsedUser.id) {
            console.log('✅ Found saved user session (will verify with backend):', parsedUser);
            cachedUser = parsedUser;
            setUser(parsedUser); // Set immediately for fast UI
            // Don't return - continue to verify with backend
          }
        } catch (e) {
          console.warn('Invalid saved user data, clearing...');
          localStorage.removeItem('phanova_user');
        }
      }

      // Check for Auth0 callback (code in URL)
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      
      if (code && state) {
        console.log('✅ Auth0 callback detected - checking backend session...');
        
        // After Auth0 callback, check backend session
        try {
          const baseUrl = apiBaseUrl || 'http://localhost:5802';
          console.log('🔧 Making API call to:', `${baseUrl}/session/user/spa_match`);
          const response = await fetch(`${baseUrl}/session/user/spa_match`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const sessionData = await response.json();
            if (sessionData.user) {
              console.log('✅ Backend session found:', sessionData.user);
              const authenticatedUser = {
                id: sessionData.user.sub || sessionData.user.id || 'auth0_user',
                email: sessionData.user.email,
                name: sessionData.user.name,
                role: sessionData.user['https://phanova.com/role'] || 'provider',
                roles: extractRoles(sessionData.user),
                is_internal: sessionData.user.is_internal || false,
                is_clinic_admin: sessionData.user.is_clinic_admin || false
              };
              
              localStorage.setItem('phanova_user', JSON.stringify(authenticatedUser));
              setUser(authenticatedUser);

              // Org selection is owned by OrganizationContext now — it watches
              // the user from useAuth() and runs its initialize() effect once
              // auth has settled.

              // Clean up URL and redirect to dummy page
              const cleanUrl = window.location.origin + '/dummy';
              window.history.replaceState({}, document.title, cleanUrl);
              window.location.href = '/dummy';
              
              setLoading(false);
              return;
            }
          }
        } catch (error) {
          console.error('Backend session check failed:', error);
        }
        
        // Auth0 callback detected but backend session check failed — show login
        console.log('❌ Auth0 callback detected but no valid backend session');
        setLoading(false);
        return;
      }

      // Check for recent auth attempt and verify backend session
      const authAttempt = localStorage.getItem('phanova_auth_attempt');
      if (authAttempt) {
        const attemptTime = parseInt(authAttempt);
        const currentTime = Date.now();
        const timeDiff = currentTime - attemptTime;
        const fiveMinutes = 5 * 60 * 1000;
        
        if (timeDiff < fiveMinutes) {
          console.log('✅ Recent auth attempt detected - checking backend session...');
          
          // Check backend session for real authentication
          try {
            const baseUrl = apiBaseUrl || 'http://localhost:5802';
            console.log('🔧 Making API call to:', `${baseUrl}/session/user/spa_match`);
            const response = await fetch(`${baseUrl}/session/user/spa_match`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              }
            });

        if (response.ok) {
          const sessionData = await response.json();
          console.log('🔍 DEBUG - Full sessionData response:', sessionData);
          if (sessionData.user) {
                console.log('✅ Valid backend session found:', sessionData.user);
                console.log('🔍 DEBUG - sessionData.user.is_internal value:', sessionData.user.is_internal);
                console.log('🔍 DEBUG - typeof sessionData.user.is_internal:', typeof sessionData.user.is_internal);
              const authenticatedUser = {
                id: sessionData.user.sub || sessionData.user.id || 'recent_auth',
                email: sessionData.user.email,
                name: sessionData.user.name,
                role: sessionData.user['https://phanova.com/role'] || 'provider',
                roles: extractRoles(sessionData.user),
                is_internal: sessionData.user.is_internal || false,
                is_clinic_admin: sessionData.user.is_clinic_admin || false
              };
                console.log('🔍 DEBUG - authenticatedUser object:', authenticatedUser);
                
                localStorage.setItem('phanova_user', JSON.stringify(authenticatedUser));
                setUser(authenticatedUser);
                setLoading(false);
                return;
              } else {
                console.log('❌ No backend session found');
              }
            } else {
              console.log('❌ Backend session check failed:', response.status);
            }
          } catch (error) {
            console.error('Backend session check error:', error);
          }
          
          // Recent auth attempt but backend check failed — show login
          console.log('❌ Recent auth attempt but no valid backend session');
          localStorage.removeItem('phanova_auth_attempt');
          setLoading(false);
          return;
        } else {
          localStorage.removeItem('phanova_auth_attempt');
        }
      }

      // Always verify cached user with backend
      if (cachedUser) {
        console.log('🔄 Verifying cached user with backend...');
        try {
          const baseUrl = apiBaseUrl || 'http://localhost:5802';
          console.log('🔧 Making API call to:', `${baseUrl}/session/user/spa_match`);
          const response = await fetch(`${baseUrl}/session/user/spa_match`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const sessionData = await response.json();
            console.log('🔍 DEBUG - Full sessionData response:', sessionData);
            if (sessionData.user) {
              console.log('✅ Backend session verified:', sessionData.user);
              console.log('🔍 DEBUG - sessionData.user.is_internal value:', sessionData.user.is_internal);
              console.log('🔍 DEBUG - typeof sessionData.user.is_internal:', typeof sessionData.user.is_internal);
              const authenticatedUser = {
                id: sessionData.user.sub || sessionData.user.id,
                email: sessionData.user.email,
                name: sessionData.user.name,
                role: sessionData.user['https://phanova.com/role'] || 'provider',
                roles: extractRoles(sessionData.user),
                is_internal: sessionData.user.is_internal || false,
                is_clinic_admin: sessionData.user.is_clinic_admin || false
              };
              console.log('🔍 DEBUG - authenticatedUser object:', authenticatedUser);
              
              localStorage.setItem('phanova_user', JSON.stringify(authenticatedUser));
              setUser(authenticatedUser);
              // Org selection is owned by OrganizationContext now.
              setLoading(false);
              return;
            } else {
              console.log('❌ No backend session found, clearing cache');
              localStorage.removeItem('phanova_user');
              setUser(null);
            }
          } else {
            console.log('❌ Backend session check failed:', response.status);
            localStorage.removeItem('phanova_user');
            setUser(null);
          }
        } catch (error) {
          console.error('Backend session check error:', error);
        }
      }

      // If no authentication found, user stays null
      console.log('❌ No authentication found');
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setLoading(false);
      isInitializedRef.current = true;
    }
  }, []);

  // Memoize login to prevent unnecessary re-renders
  const login = useCallback(() => {
    console.log('🔑 Login initiated...');
    console.log('🔧 Using API Base URL:', apiBaseUrl);
    console.log('🔧 Expected redirect URI:', redirectUri);

    // Mark auth attempt timestamp
    localStorage.setItem('phanova_auth_attempt', Date.now().toString());

    // Redirect to backend Auth0 login endpoint
    const baseUrl = apiBaseUrl || 'http://localhost:5802';
    const loginUrl = `${baseUrl}/login`;
    console.log('🔗 Redirecting to login URL:', loginUrl);
    window.location.href = loginUrl;
  }, []);

  // Memoize logout to prevent unnecessary re-renders
  const logout = useCallback(() => {
    performLogout();
  }, [performLogout]);

  // Save user to localStorage when user state changes
  // Guard: only clear localStorage after checkAuthStatus has run (isInitializedRef),
  // to avoid a race condition where the sync effect clears the cached user
  // before checkAuthStatus can read it on page reload.
  useEffect(() => {
    if (user) {
      localStorage.setItem('phanova_user', JSON.stringify(user));
      console.log('User session saved:', user);
    } else if (isInitializedRef.current) {
      localStorage.removeItem('phanova_user');
      console.log('User session cleared');
    }
  }, [user]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Memoize context value to prevent unnecessary re-renders in consumers
  // This is critical because useSessionTimeout updates state every second.
  // isWarningShown is included so dwell-time consumers can pause while the
  // session warning modal is visible (PHA-710 dwell-sync).
  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    checkAuthStatus,
    isSessionWarning: isWarningShown,
  }), [user, loading, login, logout, checkAuthStatus, isWarningShown]);

  // Memoize onExtend callback to prevent SessionTimeoutWarning re-renders.
  // Async so we can act on the result: a non-200 / network failure on a forced
  // extend means the session is dead → clean logout. Success already hid the
  // modal inside extendSession (setIsWarningShown(false)), so we must NOT call
  // hideWarning() here — that would re-enter the modal loop on failure.
  // Forced extends always bypass the in-flight guard (only background trackActivity
  // calls are gated), so false here means a genuine server failure or dead session.
  const handleExtendSession = useCallback(async () => {
    const ok = await extendSession(true);
    if (!ok) {
      // Dead session (non-200 or network failure) → one clean logout.
      // Do NOT call hideWarning() — that would re-trigger the countdown loop.
      performLogout();
    }
    // Success path: extendSession already called setIsWarningShown(false) internally.
  }, [extendSession, performLogout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutWarning
        isOpen={isWarningShown}
        remainingSeconds={remainingSeconds}
        onExtend={handleExtendSession}
        onLogout={performLogout}
      />
    </AuthContext.Provider>
  );
};

export default AuthProvider;
